import { useState, useEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Music, Trash2, Loader2, AlertCircle } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'

const AUDIO_EXTS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg'] as const
const MAX_AUDIO_BYTES = 50 * 1024 * 1024 // 50 MB

function getExt(name: string): string {
  const i = name.lastIndexOf('.')
  return i === -1 ? '' : name.slice(i).toLowerCase()
}

interface AudioManagerProps {
  modelId: string
  /** Public URL to the model's audio.mp3 (without cache-buster), or null when
   *  the model has no storage folder. */
  audioUrl: string | null
}

/**
 * Manages the optional soundtrack (audio.mp3) that lives beside a model's entry
 * file in R2. Shows a player + remove control when audio exists, and the upload
 * dropzone when it doesn't.
 */
export default function AudioManager({ modelId, audioUrl }: AudioManagerProps) {
  const { token, adminEmail } = useAuth()
  const [exists, setExists] = useState<boolean | null>(null) // null = checking
  const [version, setVersion] = useState(0) // cache-buster, bumped after mutations
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Probe R2 to see whether audio.mp3 already exists for this model.
  useEffect(() => {
    if (!audioUrl) { setExists(false); return }
    let cancelled = false
    setExists(null)
    fetch(`${audioUrl}?v=${version}`, { method: 'HEAD', cache: 'no-store' })
      .then((res) => { if (!cancelled) setExists(res.ok) })
      .catch(() => { if (!cancelled) setExists(false) })
    return () => { cancelled = true }
  }, [audioUrl, version])

  const upload = useCallback(async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/models/${modelId}/audio/upload`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Admin-Email': adminEmail!,
            'Content-Type': file.type || 'audio/mpeg',
          },
          body: file,
        },
      )
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        let detail = body
        try { detail = JSON.parse(body)?.error || body } catch { /* keep raw */ }
        throw new Error(detail || `Upload failed (${res.status})`)
      }
      setExists(true)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }, [modelId, token, adminEmail])

  const onDrop = useCallback((accepted: File[]) => {
    const file = accepted[0]
    if (!file) return
    const ext = getExt(file.name)
    if (!AUDIO_EXTS.includes(ext as (typeof AUDIO_EXTS)[number])) {
      setError(`Unsupported audio type "${ext || file.name}". Supported: ${AUDIO_EXTS.join(', ')}`)
      return
    }
    if (file.size > MAX_AUDIO_BYTES) {
      setError(`Audio is ${(file.size / (1024 * 1024)).toFixed(1)} MB; limit is ${(MAX_AUDIO_BYTES / (1024 * 1024)).toFixed(0)} MB`)
      return
    }
    upload(file)
  }, [upload])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: MAX_AUDIO_BYTES,
    disabled: busy,
  })

  const remove = async () => {
    if (!confirm('Remove the audio track for this model?')) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/models/${modelId}/audio`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Admin-Email': adminEmail!,
          },
        },
      )
      if (!res.ok) throw new Error('Failed to remove audio')
      setExists(false)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove audio')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-2">Audio File</p>

      {exists === null ? (
        <div className="flex items-center text-gray-500 text-sm py-4">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Checking for audio track...
        </div>
      ) : exists ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 border rounded-lg p-3">
            <Music className="w-6 h-6 text-gray-400 flex-shrink-0" />
            <audio
              controls
              src={audioUrl ? `${audioUrl}?v=${version}` : undefined}
              className="flex-1 min-w-0"
            />
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="text-red-600 hover:text-red-700 disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </button>
          </div>
          {/* Replace by dropping a new file */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
            } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <input {...getInputProps()} />
            <p className="text-sm text-gray-600">
              {busy ? 'Uploading...' : isDragActive ? 'Drop to replace the audio file' : 'Drag & drop to replace the audio file'}
            </p>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
          } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
        >
          <input {...getInputProps()} />
          {busy ? (
            <div className="flex items-center justify-center text-gray-600">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Uploading...
            </div>
          ) : (
            <>
              <Music className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-700 font-medium mb-1">
                {isDragActive ? 'Drop the audio file here' : 'Drag & drop your audio file here'}
              </p>
              <p className="text-sm text-gray-500">or click to select a file</p>
              <p className="text-xs text-gray-400 mt-2">
                Optional soundtrack ({AUDIO_EXTS.join(', ')}, max {(MAX_AUDIO_BYTES / (1024 * 1024)).toFixed(0)}MB).
                <br />
                <span className="text-gray-500">Stored beside the model and played on loop in the viewer.</span>
              </p>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start text-red-600 text-sm mt-2">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
